import { useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { Code, ConnectError } from '@connectrpc/connect'
import { ApprovalStrategy } from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import type { Member } from '@ebalance/proto/bffuser/v1/user_directory_model_pb'
import type {
  SubStepApprovalStage,
  SubStepInstance,
} from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { SubStepStatus } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import {
  DeletePreviousApprovalNoteRequestSchema,
  UpdatePreviousApprovalWithoutStagesNoteRequestSchema,
  UpdateSubStepApprovalNoteRequestSchema,
  UpdateSubStepApprovalWithoutStagesNoteRequestSchema,
} from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import { useAuth } from '@enechain/auth-js'
import {
  Alert,
  Badge,
  Box,
  HStack,
  Link,
  PrimaryButton,
  TertiaryButton,
  Text,
  Textarea,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaFileLinesRegularFull } from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { CompactAlert } from '~/components/common/CompactAlert'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { appToaster } from '~/libs/toaster'
import { formatDateTimeFull } from '~/models/dateTime'
import { resolveMemberDisplayName } from '~/models/member'
import {
  MEMO_MAX_LENGTH,
  memoLength,
  normalizeMemo,
  truncateMemo,
} from '~/models/memo'
import {
  approvalSettingsHref,
  isBiddingApprovalLinkedPage,
} from '~/models/workflowApprovalSettings'
import { ApprovalMemoDialog } from '~/pages/WorkflowCaseDetail/components/ApprovalMemoDialog'
import { MemoBadge } from '~/pages/WorkflowCaseDetail/components/MemoBadge'
import { MemoDialog } from '~/pages/WorkflowCaseDetail/components/MemoDialog'
import { PreviousApprovalMemoDialog } from '~/pages/WorkflowCaseDetail/components/PreviousApprovalMemoDialog'
import { useDeletePreviousApprovalNote } from '~/pages/WorkflowCaseDetail/hooks/useDeletePreviousApprovalNote'
import { useUpdatePreviousApprovalWithoutStagesNote } from '~/pages/WorkflowCaseDetail/hooks/useUpdatePreviousApprovalWithoutStagesNote'
import { useUpdateSubStepApprovalNote } from '~/pages/WorkflowCaseDetail/hooks/useUpdateSubStepApprovalNote'
import { useUpdateSubStepApprovalWithoutStagesNote } from '~/pages/WorkflowCaseDetail/hooks/useUpdateSubStepApprovalWithoutStagesNote'
import {
  type ApprovalViewerRelation,
  formatNameWithRecordKinds,
  hasNoActiveApprovers,
  needsApprovalConfirmation,
} from '~/pages/WorkflowCaseDetail/models/approvalViewerRelation'
import { resolveApprovalBlockedMessage } from '~/pages/WorkflowCaseDetail/models/serverError'
import { isManuallyCompletedPlanSubmission } from '~/pages/WorkflowCaseDetail/models/workflowCaseDetail'

type Props = {
  caseId: string
  workflowTemplateId: string
  bigStepDisplayOrder: number
  subStep: SubStepInstance
  members: Member[]
  isReadonly: boolean
  /** 承認・差戻しを押せない理由をツールチップで示す時の文言 (spec AC-WFW-003m) */
  actionDisabledTooltip?: string
  // isActionable = 承認・差戻し操作・メモ入力を受け付ける状態か。spec
  // workflow.md L507/L515/L533 で「承認待」状態限定と規定されているため、
  // 呼び出し側は sub-step が AWAITING_APPROVAL のときのみ true を渡す。
  // preserved メモの表示・編集・削除 (P0 誰でも可) はこの prop に影響されない。
  isActionable: boolean
  isPending: boolean
  onApprove: (stageOrder: number, note?: string) => Promise<void>
  onApproveWithoutStages: (note?: string) => Promise<void>
  onCancel: (memo: string) => Promise<void>
}

type EditingApproval = {
  stageOrder: number
  approverMemberId: string
  note: string | undefined
  approvedAt: Timestamp | undefined
  approverName: string
}

// 「前回承認時の申し送り」(差戻し後に保持される承認メモ) の編集対象。
// identity は (stageOrder, approverMemberId, cancelledAt) の複合
// (workflow-fix-approval-ux Design Note Decision #3)。
type EditingPreviousApproval = {
  stageOrder: number
  approverMemberId: string
  cancelledAt: Timestamp
  note: string
  approvedAt: Timestamp | undefined
  approverName: string
}

const ApprovalMemoContext = ({
  approverName,
  approvedAt,
}: {
  approverName: string
  approvedAt: Timestamp | undefined
}): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <VStack align="stretch" gap={1}>
      <Text textStyle="label" color="Text.highEmphasis">
        {t('page:workflowCaseDetail.approval.memoDialogApproverLabel')}
      </Text>
      <Box>
        <Text color="Text.highEmphasis" fontSize="sm">
          {approverName}
        </Text>
      </Box>
      <Text textStyle="label" color="Text.highEmphasis">
        {t('page:workflowCaseDetail.approval.memoDialogAtLabel')}
      </Text>
      <Text color="Text.midEmphasis" fontSize="sm">
        {approvedAt !== undefined
          ? formatDateTimeFull(timestampDate(approvedAt))
          : '-'}
      </Text>
    </VStack>
  )
}

export const ApprovalGateSection = ({
  caseId,
  workflowTemplateId,
  bigStepDisplayOrder,
  subStep,
  members,
  isReadonly,
  actionDisabledTooltip,
  isActionable,
  isPending,
  onApprove,
  onApproveWithoutStages,
  onCancel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { user } = useAuth()
  const currentMemberId = user?.memberId
  const [approvalNotes, setApprovalNotes] = useState<Record<number, string>>({})
  const [approvalWithoutStagesNote, setApprovalWithoutStagesNote] = useState('')
  const [cancelMemo, setCancelMemo] = useState('')
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  // 承認者以外・申請者本人が「承認」を押した時の確認モーダルの対象ステージ。
  const [confirmingStageOrder, setConfirmingStageOrder] = useState<
    number | undefined
  >(undefined)
  const [
    isConfirmingApprovalWithoutStages,
    setIsConfirmingApprovalWithoutStages,
  ] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  )
  const [editingApproval, setEditingApproval] = useState<
    EditingApproval | undefined
  >(undefined)
  const [editingPreviousApproval, setEditingPreviousApproval] = useState<
    EditingPreviousApproval | undefined
  >(undefined)
  const [isEditingApprovalWithoutStages, setIsEditingApprovalWithoutStages] =
    useState(false)
  const [
    editingPreviousApprovalWithoutStages,
    setEditingPreviousApprovalWithoutStages,
  ] = useState<
    | {
        approverMemberId: string
        cancelledAt: Timestamp
        note: string
        approvedAt: Timestamp | undefined
        approverName: string
      }
    | undefined
  >(undefined)
  const { mutateAsync: updateApprovalNoteAsync, isPending: isNotePending } =
    useUpdateSubStepApprovalNote(caseId)
  const {
    mutateAsync: updateApprovalWithoutStagesNoteAsync,
    isPending: isApprovalWithoutStagesNotePending,
  } = useUpdateSubStepApprovalWithoutStagesNote(caseId)
  const {
    mutateAsync: updatePreviousApprovalWithoutStagesNoteAsync,
    isPending: isPreviousApprovalWithoutStagesNotePending,
  } = useUpdatePreviousApprovalWithoutStagesNote(caseId)
  const {
    mutateAsync: deletePreviousApprovalNoteAsync,
    isPending: isPreviousNotePending,
  } = useDeletePreviousApprovalNote(caseId)

  const strategyLabel = (strategy: ApprovalStrategy): string => {
    if (strategy === ApprovalStrategy.ALL) {
      return t('page:workflowCaseDetail.approval.strategyAll')
    }
    if (strategy === ApprovalStrategy.ANY) {
      return t('page:workflowCaseDetail.approval.strategyAny')
    }
    return ''
  }

  // 承認者名の並び。承認者が 1 人も存在しないステージは undefined (spec AC-WFW-007a (a))。
  const resolveApproverNames = (
    stage: SubStepApprovalStage | undefined,
  ): string | undefined =>
    stage === undefined ||
    hasNoActiveApprovers(stage.approverMemberIds, members)
      ? undefined
      : stage.approverMemberIds
          .map((id) => resolveMemberDisplayName(members, id) ?? '-')
          .join('、')

  const openCancelDialog = (): void => {
    setCancelMemo('')
    setErrorMessage(undefined)
    setIsCancelDialogOpen(true)
  }

  const isSubStepComplete = subStep.status === SubStepStatus.COMPLETE

  // 現在のステージ (最初の未充足ステージ。backend の is_current)。差戻しの注意表示はこのステージの
  // 承認者名を出す (spec AC-WFW-007c)。
  const currentStage = subStep.approvalStages.find((stage) => stage.isCurrent)
  const cancelRelation: ApprovalViewerRelation = {
    isApproverNotApplicable:
      subStep.requiresApprovalWithoutStages ||
      subStep.viewerCancelIsApproverNotApplicable,
    isSelfApproval: subStep.viewerIsSelfApproval,
  }

  // 現在のステージを承認済みでほかの承認者を待つ間、承認・差戻しを止める事前チェック (spec AC-WFW-007a / 007c)。
  // 承認者・承認戦略は AC-WFW-007a の注意表示・確認・記録で統制する方針で、サーバーはこれらを理由に拒否しない。
  const isWaitingForOthersAfterOwnApproval =
    isActionable &&
    !isSubStepComplete &&
    (currentStage?.approved.some(
      (a) => a.memberId === (currentMemberId ?? ''),
    ) ??
      false)

  // 承認者でない・申請者本人の注意は、差戻し (gate) と承認 (stage) で同じ文が重ならないよう、
  // gate の 1 か所に 1 つの Alert で出す (alert-placement §3)。どちらかの操作に当てはまれば出し、
  // 承認者でなく申請者本人でもある時は 2 本に分けず 1 文にまとめる。
  const canCancelNotice =
    !isSubStepComplete &&
    isActionable &&
    !isReadonly &&
    !isWaitingForOthersAfterOwnApproval
  const approvableStages = subStep.approvalStages.filter(
    (stage) =>
      isActionable &&
      stage.isUnlocked &&
      !stage.isSatisfied &&
      !isSubStepComplete &&
      !stage.approved.some((a) => a.memberId === (currentMemberId ?? '')),
  )
  const notApproverStage = approvableStages.find(
    (stage) => stage.viewerIsApproverNotApplicable,
  )
  const showNotApprover =
    (canCancelNotice && cancelRelation.isApproverNotApplicable) ||
    notApproverStage !== undefined
  const showSelfApproval =
    subStep.viewerIsSelfApproval &&
    ((canCancelNotice && cancelRelation.isSelfApproval) ||
      approvableStages.length > 0)
  // 承認者の名前は注意文に入れない (2026-10-20 デザイナー指示: Alert に個別の値を並べない)。
  // 同じセクションの段階ごとの「承認者: …」 の行に出ている (UI 辞書 alert-placement.md)。
  // [spec 差異] spec AC-WFW-007a は「（承認者: {承認者名}）」 を添えるとする
  const gateNotice = showNotApprover
    ? t(
        showSelfApproval
          ? 'page:workflowCaseDetail.approval.notApproverAndSelfWarning'
          : 'page:workflowCaseDetail.approval.notApproverWarning',
      )
    : showSelfApproval
      ? t('page:workflowCaseDetail.approval.selfApprovalWarning')
      : undefined

  const handleApproveClick = async (
    stage: SubStepApprovalStage,
    noteDraft: string,
  ): Promise<void> => {
    const note = normalizeMemo(noteDraft)
    setErrorMessage(undefined)
    try {
      await onApprove(stage.stageOrder, note)
      setApprovalNotes((prev) => ({ ...prev, [stage.stageOrder]: '' }))
      // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
      showSuccessToast({
        title: t('page:workflowCaseDetail.approvalToast.approveToast'),
      })
    } catch (err) {
      // 承認 RPC 失敗時は下書きを保持して再入力可能にする
      const blockedMessage =
        err instanceof ConnectError
          ? resolveApprovalBlockedMessage(err, t)
          : undefined
      if (blockedMessage !== undefined) {
        setErrorMessage(blockedMessage)
      } else if (
        err instanceof ConnectError &&
        err.code === Code.FailedPrecondition
      ) {
        setErrorMessage(t('page:workflowCaseDetail.error.failedPrecondition'))
      } else {
        setErrorMessage(t('page:workflowCaseDetail.error.approvalFailed'))
      }
    }
  }

  const handleApproveWithoutStages = async (): Promise<void> => {
    const note = normalizeMemo(approvalWithoutStagesNote)
    setErrorMessage(undefined)
    try {
      await onApproveWithoutStages(note)
      setApprovalWithoutStagesNote('')
      showSuccessToast({
        title: t('page:workflowCaseDetail.approvalToast.approveToast'),
      })
    } catch (err) {
      const blockedMessage =
        err instanceof ConnectError
          ? resolveApprovalBlockedMessage(err, t)
          : undefined
      setErrorMessage(
        blockedMessage ??
          (err instanceof ConnectError && err.code === Code.FailedPrecondition
            ? t('page:workflowCaseDetail.error.failedPrecondition')
            : t('page:workflowCaseDetail.error.approvalFailed')),
      )
    }
  }

  const handleCancelConfirm = async (): Promise<void> => {
    const memo = cancelMemo.trim()
    if (memo === '') return
    setErrorMessage(undefined)
    try {
      await onCancel(memo)
      setIsCancelDialogOpen(false)
      setCancelMemo('')
      showSuccessToast({
        title: t('page:workflowCaseDetail.approvalToast.sendBackToast'),
      })
    } catch (err) {
      // 差戻し RPC 失敗時はダイアログを開いたまま再入力可能にする
      if (err instanceof ConnectError && err.code === Code.FailedPrecondition) {
        setErrorMessage(t('page:workflowCaseDetail.error.failedPrecondition'))
      } else {
        setErrorMessage(t('page:workflowCaseDetail.error.sendBackFailed'))
      }
    }
  }

  const confirmingStage =
    confirmingStageOrder === undefined
      ? undefined
      : subStep.approvalStages.find(
          (stage) => stage.stageOrder === confirmingStageOrder,
        )
  const confirmingRelation: ApprovalViewerRelation = {
    isApproverNotApplicable:
      confirmingStage?.viewerIsApproverNotApplicable ?? false,
    isSelfApproval: subStep.viewerIsSelfApproval,
  }

  return (
    <Box
      padding="8px 12px"
      background="Surface.secondary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="base"
    >
      {errorMessage !== undefined && (
        <CompactAlert role="alert" status="error" title={errorMessage} />
      )}

      {!isSubStepComplete && (
        <HStack align="center" justify="space-between" wrap="wrap">
          {subStep.completedAt !== undefined &&
            subStep.completedBy !== undefined && (
              <Text color="Text.midEmphasis" fontSize="sm">
                {t(
                  isManuallyCompletedPlanSubmission(subStep)
                    ? 'page:workflowCaseDetail.subStep.executorCompletedManually'
                    : 'page:workflowCaseDetail.subStep.executorCompleted',
                  {
                    // 解決できない場合も生の member ID (auth0|...) は表示しない
                    by: formatNameWithRecordKinds(
                      t,
                      resolveMemberDisplayName(members, subStep.completedBy) ??
                        '-',
                      {
                        isAssigneeNotApplicable:
                          subStep.completedIsAssigneeNotApplicable,
                      },
                    ),
                    at: formatDateTimeFull(timestampDate(subStep.completedAt)),
                  },
                )}
              </Text>
            )}
          {/* 差戻しは承認ボタンと同じ条件で活性にする。承認者以外・申請者本人には
              常時の注意表示 (下) と確認ダイアログで注意を出す (spec AC-WFW-007c)。 */}
          <ButtonTooltip content={actionDisabledTooltip}>
            <TertiaryButton
              disabled={
                isReadonly ||
                isPending ||
                !isActionable ||
                isWaitingForOthersAfterOwnApproval
              }
              onClick={openCancelDialog}
              size="sm"
            >
              {t('page:workflowCaseDetail.approval.cancel')}
            </TertiaryButton>
          </ButtonTooltip>
        </HStack>
      )}
      {isWaitingForOthersAfterOwnApproval && (
        <Text color="Text.midEmphasis" fontSize="sm">
          {t('page:workflowCaseDetail.approval.disabledApprovedWaitingOthers')}
        </Text>
      )}
      {gateNotice !== undefined && (
        <Box marginTop={1}>
          <CompactAlert status="warning" title={gateNotice} />
        </Box>
      )}

      {(subStep.requiresApprovalWithoutStages ||
        subStep.approvalWithoutStages !== undefined ||
        subStep.previousApprovalWithoutStagesNotes.length > 0) && (
        <VStack align="stretch" gap={1} marginTop={2}>
          {subStep.requiresApprovalWithoutStages && (
            <Alert status="error">
              <VStack align="stretch" gap={2}>
                <Text>
                  {t(
                    isBiddingApprovalLinkedPage(subStep.linkedPageKind)
                      ? 'page:workflowCaseDetail.approval.approvalStagesNotConfiguredApprovalFlow'
                      : 'page:workflowCaseDetail.approval.approvalStagesNotConfiguredWorkflowTemplate',
                  )}
                </Text>
                {workflowTemplateId !== '' && (
                  <Link asChild>
                    <RouterLink
                      rel="noopener noreferrer"
                      target="_blank"
                      to={approvalSettingsHref(
                        subStep.linkedPageKind,
                        workflowTemplateId,
                      )}
                    >
                      {t(
                        isBiddingApprovalLinkedPage(subStep.linkedPageKind)
                          ? 'page:workflowCaseDetail.header.approvalFlowSettingsLink'
                          : 'page:workflowCaseDetail.approval.approvalStagesNotConfiguredWorkflowTemplateLink',
                      )}
                    </RouterLink>
                  </Link>
                )}
              </VStack>
            </Alert>
          )}
          {subStep.approvalWithoutStages === undefined ? (
            subStep.requiresApprovalWithoutStages && (
              <>
                <HStack align="center" gap={2} marginTop={1}>
                  <ButtonTooltip content={actionDisabledTooltip} focusable>
                    <PrimaryButton
                      disabled={
                        !isActionable ||
                        isReadonly ||
                        isPending ||
                        isSubStepComplete
                      }
                      onClick={() => {
                        setIsConfirmingApprovalWithoutStages(true)
                      }}
                      size="sm"
                    >
                      {t('page:workflowCaseDetail.approval.approve')}
                    </PrimaryButton>
                  </ButtonTooltip>
                </HStack>
                {isActionable && !isReadonly && !isSubStepComplete && (
                  <VStack align="stretch" gap={1} marginTop={1}>
                    <Text
                      textStyle="label"
                      color="Text.highEmphasis"
                      fontSize="xs"
                    >
                      {t('page:workflowCaseDetail.approval.noteLabel')}
                    </Text>
                    <Textarea
                      aria-label={t(
                        'page:workflowCaseDetail.approval.noteLabel',
                      )}
                      disabled={isPending}
                      onChange={(e) => {
                        setApprovalWithoutStagesNote(
                          truncateMemo(e.target.value, MEMO_MAX_LENGTH),
                        )
                      }}
                      placeholder={t(
                        'page:workflowCaseDetail.approval.notePlaceholder',
                      )}
                      rows={2}
                      value={approvalWithoutStagesNote}
                    />
                    <Text
                      color="Text.lowEmphasis"
                      fontSize="xs"
                      textAlign="right"
                    >
                      {t('page:workflowCaseDetail.subStep.memo.counter', {
                        count: memoLength(approvalWithoutStagesNote),
                        max: MEMO_MAX_LENGTH,
                      })}
                    </Text>
                  </VStack>
                )}
              </>
            )
          ) : (
            <VStack align="stretch" gap={1}>
              <HStack align="center" gap={2}>
                <Badge status="positive">
                  {t('page:workflowCaseDetail.approval.approved')}
                </Badge>
                <Text color="Text.midEmphasis" fontSize="sm">
                  {formatNameWithRecordKinds(
                    t,
                    resolveMemberDisplayName(
                      members,
                      subStep.approvalWithoutStages.memberId,
                    ) ?? '-',
                    subStep.approvalWithoutStages,
                  )}
                </Text>
                {subStep.approvalWithoutStages.approvedAt !== undefined && (
                  <Text color="Text.midEmphasis" fontSize="sm">
                    {formatDateTimeFull(
                      timestampDate(subStep.approvalWithoutStages.approvedAt),
                    )}
                  </Text>
                )}
              </HStack>
              {subStep.approvalWithoutStages.note === undefined ||
              subStep.approvalWithoutStages.note === '' ? (
                <TertiaryButton
                  alignSelf="flex-start"
                  disabled={isApprovalWithoutStagesNotePending}
                  onClick={() => {
                    setIsEditingApprovalWithoutStages(true)
                  }}
                  size="sm"
                >
                  <FaFileLinesRegularFull />
                  {t('page:workflowCaseDetail.approval.addMemoButton')}
                </TertiaryButton>
              ) : (
                <MemoBadge
                  deletable
                  editable
                  isPending={isApprovalWithoutStagesNotePending}
                  label={t('page:workflowCaseDetail.approval.memoBadgeLabel', {
                    name:
                      resolveMemberDisplayName(
                        members,
                        subStep.approvalWithoutStages.memberId,
                      ) ?? '-',
                  })}
                  memo={subStep.approvalWithoutStages.note}
                  onDelete={async () => {
                    try {
                      await updateApprovalWithoutStagesNoteAsync(
                        create(
                          UpdateSubStepApprovalWithoutStagesNoteRequestSchema,
                          {
                            workflowCaseId: caseId,
                            bigStepDisplayOrder,
                            subStepDisplayOrder: subStep.displayOrder,
                          },
                        ),
                      )
                      showSuccessToast({
                        title: t(
                          'page:workflowCaseDetail.approvalToast.memoDeleteToast',
                        ),
                      })
                    } catch {
                      appToaster.create({
                        type: 'error',
                        title: t('page:workflowCaseDetail.error.unknown'),
                      })
                    }
                  }}
                  onEdit={() => {
                    setIsEditingApprovalWithoutStages(true)
                  }}
                />
              )}
            </VStack>
          )}
          {subStep.previousApprovalWithoutStagesNotes.map((note) => {
            const cancelledAt = note.cancelledAt
            if (cancelledAt === undefined) return null
            const approverName =
              resolveMemberDisplayName(members, note.memberId) ?? '-'
            return (
              <MemoBadge
                key={`${note.memberId}-${timestampDate(cancelledAt).toISOString()}`}
                date={
                  note.approvedAt === undefined
                    ? undefined
                    : formatDateTimeFull(timestampDate(note.approvedAt))
                }
                deletable
                editable
                isPending={isPreviousApprovalWithoutStagesNotePending}
                label={t(
                  'page:workflowCaseDetail.approval.memoBadgeLabelPreserved',
                  {
                    name: approverName,
                  },
                )}
                memo={note.note}
                onDelete={async () => {
                  try {
                    await updatePreviousApprovalWithoutStagesNoteAsync(
                      create(
                        UpdatePreviousApprovalWithoutStagesNoteRequestSchema,
                        {
                          workflowCaseId: caseId,
                          bigStepDisplayOrder,
                          subStepDisplayOrder: subStep.displayOrder,
                          approverMemberId: note.memberId,
                          cancelledAt: note.cancelledAt,
                        },
                      ),
                    )
                    showSuccessToast({
                      title: t(
                        'page:workflowCaseDetail.approvalToast.memoDeleteToast',
                      ),
                    })
                  } catch {
                    appToaster.create({
                      type: 'error',
                      title: t('page:workflowCaseDetail.error.unknown'),
                    })
                  }
                }}
                onEdit={() => {
                  setEditingPreviousApprovalWithoutStages({
                    approverMemberId: note.memberId,
                    cancelledAt,
                    note: note.note,
                    approvedAt: note.approvedAt,
                    approverName,
                  })
                }}
              />
            )
          })}
        </VStack>
      )}
      {subStep.approvalStages.length > 0 && (
        <VStack align="stretch" gap={1} marginTop={2}>
          {subStep.approvalStages.map((stage) => {
            const isApproved = stage.isSatisfied

            // 順序ゲート: 前段階が未承認の場合、当該 stage の承認は不可 (spec AC-WFW-007a)。
            // 直接 RPC された場合は backend で FailedPrecondition を返す。
            const isStageEnabled = stage.isUnlocked

            const relation: ApprovalViewerRelation = {
              isApproverNotApplicable: stage.viewerIsApproverNotApplicable,
              isSelfApproval: subStep.viewerIsSelfApproval,
            }
            const hasCurrentMemberApproved = stage.approved.some(
              (a) => a.memberId === (currentMemberId ?? ''),
            )
            // isActionable: 承認・メモ入力は「承認待」状態のみ (spec workflow.md
            // L507/L515/L533)。差戻し直後 (IN_PROGRESS) に preserved メモを表示
            // している間も、この gate で承認ボタン / Textarea を disable する。
            // 閲覧者が承認者リストに含まれるかでは活性/非活性を切り替えない
            // (spec AC-WFW-007a)。
            const canApprove =
              isActionable &&
              isStageEnabled &&
              !hasCurrentMemberApproved &&
              !isApproved &&
              !isSubStepComplete
            // 承認ボタンを非活性で出している間は押せない理由を文で出す (spec
            // AC-WFW-007a)。承認済ステージ・完了サブステップはボタンを出さない。
            const disabledReason =
              !isActionable || isApproved || isSubStepComplete
                ? undefined
                : !isStageEnabled
                  ? t('page:workflowCaseDetail.approval.disabledPreviousStage')
                  : hasCurrentMemberApproved
                    ? t(
                        'page:workflowCaseDetail.approval.disabledApprovedWaitingOthers',
                      )
                    : undefined
            // member ID を表示名に解決する (解決できない場合も生の
            // member ID (auth0|...) は表示しない)
            const approverNames = resolveApproverNames(stage)
            const noteDraft = approvalNotes[stage.stageOrder] ?? ''
            const noteLength = memoLength(noteDraft)

            return (
              <Box
                key={stage.stageOrder}
                padding="8px 12px"
                background="Surface.primary"
                borderWidth="1px"
                borderColor="Border.lowEmphasis"
                borderRadius="base"
              >
                <VStack align="stretch" gap={1}>
                  {/* eslint-disable-next-line ebalance/no-adhoc-typography -- design
                      .substep-approval-strategy (case-detail.html:235) = xs/600 と一致。
                      1 箇所のみの組のため role 追加せず例外化 */}
                  <Text
                    color="Text.midEmphasis"
                    fontSize="xs"
                    fontWeight="semibold"
                  >
                    {subStep.approvalStages.length > 1 &&
                      t('page:workflowCaseDetail.approval.stage', {
                        order: stage.stageOrder,
                      })}
                    {strategyLabel(stage.strategy)}
                  </Text>
                  <Text color="Text.highEmphasis" fontSize="sm">
                    {approverNames === undefined
                      ? t('page:workflowCaseDetail.approval.noApprovers')
                      : t('page:workflowCaseDetail.approval.approvers', {
                          names: approverNames,
                        })}
                  </Text>
                  <HStack align="center" gap={2} marginTop={1}>
                    {isApproved ? (
                      <>
                        <Badge status="positive">
                          {t('page:workflowCaseDetail.approval.approved')}
                        </Badge>
                        {/* 承認ごとに「承認者名 + その承認の承認日時」を並べる
                            (spec AC-WFW-007a/007b、design demand/case-detail.html
                            .substep-actor-name → .substep-actor-time)。 日時は
                            承認メモの有無に関わらず出す。 */}
                        {stage.approved.map((a) => {
                          const approverName =
                            resolveMemberDisplayName(members, a.memberId) ?? '-'
                          return (
                            <HStack
                              key={a.memberId}
                              align="center"
                              gap={1}
                              aria-label={approverName}
                              role="group"
                            >
                              <Text color="Text.midEmphasis" fontSize="sm">
                                {formatNameWithRecordKinds(t, approverName, a)}
                              </Text>
                              {a.approvedAt !== undefined && (
                                <Text color="Text.midEmphasis" fontSize="sm">
                                  {formatDateTimeFull(
                                    timestampDate(a.approvedAt),
                                  )}
                                </Text>
                              )}
                            </HStack>
                          )
                        })}
                      </>
                    ) : (
                      <ButtonTooltip content={actionDisabledTooltip}>
                        <PrimaryButton
                          disabled={!canApprove || isReadonly || isPending}
                          onClick={() => {
                            if (needsApprovalConfirmation(relation)) {
                              setConfirmingStageOrder(stage.stageOrder)
                              return
                            }
                            void handleApproveClick(stage, noteDraft)
                          }}
                          size="sm"
                        >
                          {t('page:workflowCaseDetail.approval.approve')}
                        </PrimaryButton>
                      </ButtonTooltip>
                    )}
                  </HStack>
                  {disabledReason !== undefined && (
                    <Text color="Text.midEmphasis" fontSize="sm">
                      {disabledReason}
                    </Text>
                  )}
                  {!isApproved && canApprove && (
                    <VStack align="stretch" gap={1} marginTop={1}>
                      <Text
                        textStyle="label"
                        color="Text.highEmphasis"
                        fontSize="xs"
                      >
                        {t('page:workflowCaseDetail.approval.noteLabel')}
                      </Text>
                      <Textarea
                        aria-label={t(
                          'page:workflowCaseDetail.approval.noteLabel',
                        )}
                        onChange={(e) => {
                          setApprovalNotes((prev) => ({
                            ...prev,
                            [stage.stageOrder]: truncateMemo(
                              e.target.value,
                              MEMO_MAX_LENGTH,
                            ),
                          }))
                        }}
                        placeholder={t(
                          'page:workflowCaseDetail.approval.notePlaceholder',
                        )}
                        rows={2}
                        value={noteDraft}
                      />
                      <Text
                        color={
                          noteLength >= MEMO_MAX_LENGTH
                            ? 'Text.negativeHigh'
                            : 'Text.lowEmphasis'
                        }
                        fontSize="xs"
                        textAlign="right"
                      >
                        {noteLength >= MEMO_MAX_LENGTH &&
                          `${t('page:workflowCaseDetail.subStep.memo.counterMax')} `}
                        {t('page:workflowCaseDetail.subStep.memo.counter', {
                          count: noteLength,
                          max: MEMO_MAX_LENGTH,
                        })}
                      </Text>
                    </VStack>
                  )}
                  {stage.approved.length > 0 && (
                    <VStack align="stretch" gap={1} marginTop={1}>
                      {stage.approved.map((a) => {
                        const approverName =
                          resolveMemberDisplayName(members, a.memberId) ?? '-'
                        const hasNote = a.note !== undefined && a.note !== ''
                        return hasNote ? (
                          <MemoBadge
                            key={a.memberId}
                            date={
                              a.approvedAt !== undefined
                                ? formatDateTimeFull(
                                    timestampDate(a.approvedAt),
                                  )
                                : undefined
                            }
                            deletable
                            editable
                            isPending={isNotePending}
                            label={t(
                              'page:workflowCaseDetail.approval.memoBadgeLabel',
                              { name: approverName },
                            )}
                            memo={a.note ?? ''}
                            onDelete={async () => {
                              try {
                                await updateApprovalNoteAsync(
                                  create(
                                    UpdateSubStepApprovalNoteRequestSchema,
                                    {
                                      workflowCaseId: caseId,
                                      bigStepDisplayOrder,
                                      subStepDisplayOrder: subStep.displayOrder,
                                      stageOrder: stage.stageOrder,
                                      approverMemberId: a.memberId,
                                    },
                                  ),
                                )
                                // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
                                showSuccessToast({
                                  title: t(
                                    'page:workflowCaseDetail.approvalToast.memoDeleteToast',
                                  ),
                                })
                              } catch (err) {
                                if (
                                  err instanceof ConnectError &&
                                  err.code === Code.Aborted
                                ) {
                                  appToaster.create({
                                    type: 'error',
                                    title: t(
                                      'page:workflowCaseDetail.error.aborted',
                                    ),
                                  })
                                } else {
                                  appToaster.create({
                                    type: 'error',
                                    title: t(
                                      'page:workflowCaseDetail.error.unknown',
                                    ),
                                  })
                                }
                              }
                            }}
                            onEdit={() => {
                              setEditingApproval({
                                stageOrder: stage.stageOrder,
                                approverMemberId: a.memberId,
                                note: a.note,
                                approvedAt: a.approvedAt,
                                approverName,
                              })
                            }}
                          />
                        ) : (
                          <TertiaryButton
                            key={a.memberId}
                            alignSelf="flex-start"
                            disabled={isNotePending}
                            onClick={() => {
                              setEditingApproval({
                                stageOrder: stage.stageOrder,
                                approverMemberId: a.memberId,
                                note: undefined,
                                approvedAt: a.approvedAt,
                                approverName,
                              })
                            }}
                            size="sm"
                          >
                            <FaFileLinesRegularFull />
                            {t(
                              'page:workflowCaseDetail.approval.addMemoButton',
                            )}
                          </TertiaryButton>
                        )
                      })}
                    </VStack>
                  )}
                  {/* 前回承認時の申し送り (spec AC-WFW-007c/d, Decision #1/#3):
                      差戻し後も承認メモを active と同じ editable/deletable
                      MemoBadge で表示し、「(前回承認時、差戻し済み)」補足を
                      label に含める。 */}
                  {stage.previousNotes.length > 0 && (
                    <VStack align="stretch" gap={1} marginTop={1}>
                      {stage.previousNotes.map((n) => {
                        const approverName =
                          resolveMemberDisplayName(members, n.memberId) ?? '-'
                        return (
                          <MemoBadge
                            key={`${n.memberId}-${n.cancelledAt !== undefined ? timestampDate(n.cancelledAt).toISOString() : ''}`}
                            date={
                              n.approvedAt !== undefined
                                ? formatDateTimeFull(
                                    timestampDate(n.approvedAt),
                                  )
                                : undefined
                            }
                            deletable
                            editable
                            isPending={isPreviousNotePending}
                            label={t(
                              'page:workflowCaseDetail.approval.memoBadgeLabelPreserved',
                              { name: approverName },
                            )}
                            memo={n.note}
                            onDelete={async () => {
                              if (n.cancelledAt === undefined) return
                              try {
                                await deletePreviousApprovalNoteAsync(
                                  create(
                                    DeletePreviousApprovalNoteRequestSchema,
                                    {
                                      workflowCaseId: caseId,
                                      bigStepDisplayOrder,
                                      subStepDisplayOrder: subStep.displayOrder,
                                      stageOrder: stage.stageOrder,
                                      approverMemberId: n.memberId,
                                      cancelledAt: n.cancelledAt,
                                    },
                                  ),
                                )
                                // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
                                showSuccessToast({
                                  title: t(
                                    'page:workflowCaseDetail.approvalToast.memoDeleteToast',
                                  ),
                                })
                              } catch (err) {
                                if (
                                  err instanceof ConnectError &&
                                  err.code === Code.Aborted
                                ) {
                                  appToaster.create({
                                    type: 'error',
                                    title: t(
                                      'page:workflowCaseDetail.error.aborted',
                                    ),
                                  })
                                } else {
                                  appToaster.create({
                                    type: 'error',
                                    title: t(
                                      'page:workflowCaseDetail.error.unknown',
                                    ),
                                  })
                                }
                              }
                            }}
                            onEdit={() => {
                              if (n.cancelledAt === undefined) return
                              setEditingPreviousApproval({
                                stageOrder: stage.stageOrder,
                                approverMemberId: n.memberId,
                                cancelledAt: n.cancelledAt,
                                note: n.note,
                                approvedAt: n.approvedAt,
                                approverName,
                              })
                            }}
                          />
                        )
                      })}
                    </VStack>
                  )}
                </VStack>
              </Box>
            )
          })}
        </VStack>
      )}
      {isCancelDialogOpen && (
        <Modal
          footer={
            <SaveCancelFooter
              isPending={isPending}
              isSaveDisabled={cancelMemo.trim() === ''}
              onCancel={() => {
                setIsCancelDialogOpen(false)
              }}
              onSave={() => {
                void handleCancelConfirm()
              }}
              saveLabel={t(
                'page:workflowCaseDetail.approval.cancelConfirmLabel',
              )}
            />
          }
          isOpen={isCancelDialogOpen}
          onClose={() => {
            setIsCancelDialogOpen(false)
          }}
          size="sm"
          title={t('page:workflowCaseDetail.approval.cancelDialogTitle')}
        >
          <VStack align="stretch" gap={3}>
            {errorMessage !== undefined && (
              <Alert status="error" title={errorMessage} />
            )}
            {cancelRelation.isApproverNotApplicable && (
              <Alert
                status="warning"
                title={t(
                  'page:workflowCaseDetail.approval.notApproverConfirmCancelBody',
                )}
              />
            )}
            {cancelRelation.isSelfApproval && (
              <Alert
                status="warning"
                title={t(
                  'page:workflowCaseDetail.approval.selfApprovalConfirmCancelBody',
                )}
              />
            )}
            <Text textStyle="label" color="Text.highEmphasis">
              {t('page:workflowCaseDetail.approval.cancelMemoLabel')}
            </Text>
            <Textarea
              aria-label={t('page:workflowCaseDetail.approval.cancelMemoLabel')}
              disabled={isPending}
              onChange={(e) => {
                setCancelMemo(truncateMemo(e.target.value, MEMO_MAX_LENGTH))
              }}
              placeholder={t(
                'page:workflowCaseDetail.approval.cancelMemoPlaceholder',
              )}
              rows={4}
              value={cancelMemo}
            />
            <Text
              color={
                memoLength(cancelMemo) >= MEMO_MAX_LENGTH
                  ? 'Text.negativeHigh'
                  : 'Text.lowEmphasis'
              }
              fontSize="xs"
              textAlign="right"
            >
              {memoLength(cancelMemo) >= MEMO_MAX_LENGTH &&
                `${t('page:workflowCaseDetail.subStep.memo.counterMax')} `}
              {t('page:workflowCaseDetail.subStep.memo.counter', {
                count: memoLength(cancelMemo),
                max: MEMO_MAX_LENGTH,
              })}
            </Text>
          </VStack>
        </Modal>
      )}
      {isConfirmingApprovalWithoutStages && (
        <Modal
          footer={
            <SaveCancelFooter
              isPending={isPending}
              onCancel={() => {
                setIsConfirmingApprovalWithoutStages(false)
              }}
              onSave={() => {
                setIsConfirmingApprovalWithoutStages(false)
                void handleApproveWithoutStages()
              }}
              saveLabel={t(
                'page:workflowCaseDetail.approval.approveConfirmLabel',
              )}
            />
          }
          isOpen
          onClose={() => {
            setIsConfirmingApprovalWithoutStages(false)
          }}
          size="sm"
          title={t('page:workflowCaseDetail.approval.approveDialogTitle')}
        >
          <VStack align="stretch" gap={2}>
            <Alert status="warning">
              {t(
                'page:workflowCaseDetail.approval.notApproverConfirmApproveBody',
              )}
            </Alert>
            {cancelRelation.isSelfApproval && (
              <Alert status="warning">
                {t(
                  'page:workflowCaseDetail.approval.selfApprovalConfirmApproveBody',
                )}
              </Alert>
            )}
          </VStack>
        </Modal>
      )}
      {isEditingApprovalWithoutStages &&
        subStep.approvalWithoutStages !== undefined && (
          <MemoDialog
            context={
              <ApprovalMemoContext
                approvedAt={subStep.approvalWithoutStages.approvedAt}
                approverName={
                  resolveMemberDisplayName(
                    members,
                    subStep.approvalWithoutStages.memberId,
                  ) ?? '-'
                }
              />
            }
            initialMemo={subStep.approvalWithoutStages.note}
            isOpen
            isPending={isApprovalWithoutStagesNotePending}
            mapError={(err) =>
              err instanceof ConnectError && err.code === Code.Aborted
                ? t('page:workflowCaseDetail.error.aborted')
                : undefined
            }
            onClose={() => {
              setIsEditingApprovalWithoutStages(false)
            }}
            onSave={async (memo) => {
              await updateApprovalWithoutStagesNoteAsync(
                create(UpdateSubStepApprovalWithoutStagesNoteRequestSchema, {
                  workflowCaseId: caseId,
                  bigStepDisplayOrder,
                  subStepDisplayOrder: subStep.displayOrder,
                  note: memo === '' ? undefined : memo,
                }),
              )
              showSuccessToast({
                title: t('page:workflowCaseDetail.approvalToast.memoSaveToast'),
              })
            }}
            placeholder={t(
              'page:workflowCaseDetail.approval.memoDialogPlaceholder',
            )}
            saveLabel={t('page:workflowCaseDetail.approval.memoDialogSave')}
            textareaLabel={t(
              'page:workflowCaseDetail.approval.memoDialogTextareaLabel',
            )}
            title={t('page:workflowCaseDetail.approval.memoDialogTitle')}
          />
        )}
      {editingPreviousApprovalWithoutStages !== undefined && (
        <MemoDialog
          context={
            <ApprovalMemoContext
              approvedAt={editingPreviousApprovalWithoutStages.approvedAt}
              approverName={editingPreviousApprovalWithoutStages.approverName}
            />
          }
          initialMemo={editingPreviousApprovalWithoutStages.note}
          isOpen
          isPending={isPreviousApprovalWithoutStagesNotePending}
          mapError={(err) =>
            err instanceof ConnectError && err.code === Code.Aborted
              ? t('page:workflowCaseDetail.error.aborted')
              : undefined
          }
          onClose={() => {
            setEditingPreviousApprovalWithoutStages(undefined)
          }}
          onSave={async (memo) => {
            await updatePreviousApprovalWithoutStagesNoteAsync(
              create(UpdatePreviousApprovalWithoutStagesNoteRequestSchema, {
                workflowCaseId: caseId,
                bigStepDisplayOrder,
                subStepDisplayOrder: subStep.displayOrder,
                approverMemberId:
                  editingPreviousApprovalWithoutStages.approverMemberId,
                cancelledAt: editingPreviousApprovalWithoutStages.cancelledAt,
                note: memo === '' ? undefined : memo,
              }),
            )
            showSuccessToast({
              title: t('page:workflowCaseDetail.approvalToast.memoSaveToast'),
            })
          }}
          placeholder={t(
            'page:workflowCaseDetail.approval.memoDialogPlaceholder',
          )}
          saveLabel={t('page:workflowCaseDetail.approval.memoDialogSave')}
          textareaLabel={t(
            'page:workflowCaseDetail.approval.memoDialogTextareaLabel',
          )}
          title={t('page:workflowCaseDetail.approval.memoDialogTitle')}
        />
      )}
      {confirmingStage !== undefined && (
        <Modal
          footer={
            <SaveCancelFooter
              isPending={isPending}
              onCancel={() => {
                setConfirmingStageOrder(undefined)
              }}
              onSave={() => {
                setConfirmingStageOrder(undefined)
                void handleApproveClick(
                  confirmingStage,
                  approvalNotes[confirmingStage.stageOrder] ?? '',
                )
              }}
              saveLabel={t(
                'page:workflowCaseDetail.approval.approveConfirmLabel',
              )}
            />
          }
          isOpen
          onClose={() => {
            setConfirmingStageOrder(undefined)
          }}
          size="sm"
          title={t('page:workflowCaseDetail.approval.approveDialogTitle')}
        >
          <VStack align="stretch" gap={2}>
            {confirmingRelation.isApproverNotApplicable && (
              <Alert
                status="warning"
                title={t(
                  'page:workflowCaseDetail.approval.notApproverConfirmApproveBody',
                )}
              />
            )}
            {confirmingRelation.isSelfApproval && (
              <Alert
                status="warning"
                title={t(
                  'page:workflowCaseDetail.approval.selfApprovalConfirmApproveBody',
                )}
              />
            )}
          </VStack>
        </Modal>
      )}
      {editingApproval !== undefined && (
        <ApprovalMemoDialog
          approvedAt={editingApproval.approvedAt}
          approverMemberId={editingApproval.approverMemberId}
          approverName={editingApproval.approverName}
          bigStepDisplayOrder={bigStepDisplayOrder}
          caseId={caseId}
          initialNote={editingApproval.note}
          isOpen={editingApproval !== undefined}
          onClose={() => {
            setEditingApproval(undefined)
          }}
          stageOrder={editingApproval.stageOrder}
          subStepDisplayOrder={subStep.displayOrder}
        />
      )}
      {editingPreviousApproval !== undefined && (
        <PreviousApprovalMemoDialog
          approvedAt={editingPreviousApproval.approvedAt}
          approverMemberId={editingPreviousApproval.approverMemberId}
          approverName={editingPreviousApproval.approverName}
          bigStepDisplayOrder={bigStepDisplayOrder}
          cancelledAt={editingPreviousApproval.cancelledAt}
          caseId={caseId}
          initialNote={editingPreviousApproval.note}
          isOpen={editingPreviousApproval !== undefined}
          onClose={() => {
            setEditingPreviousApproval(undefined)
          }}
          stageOrder={editingPreviousApproval.stageOrder}
          subStepDisplayOrder={subStep.displayOrder}
        />
      )}
    </Box>
  )
}
